import React, { useContext, useCallback, useRef, useEffect } from 'react';
import Share from 'components/Dashboard/components/Share';
import { store } from 'components/Store';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import TextSelector from 'components/Common/TextSelector';
import ContentSummary from 'components/Dashboard/components/ContentSummary';
import isValidURL from 'components/Dashboard/utilities/isValidURL';
import adjustPDFScale from 'components/Dashboard/utilities/adjustPDFScale';
import useTitleScrolledStyles from 'components/Dashboard/hooks/useTitleScrolledStyles';
import useCreateMarkup from 'components/Dashboard/hooks/useCreateMarkup';
import time from 'utils/time';
import RateUpdatesContentButton from 'components/Dashboard/RateUpdatesFeed/RateUpdatesContentButton';
import ContentPagePriorityBadge from 'components/Dashboard/ItemController/ContentPagePriorityBadge';

const ShareButtonRender = (props) => {
  const { currentNew, shareButtonWidget, embed } = props;
  const { id, hansardData, title, questionFor, canonicalUrl, objectId, objectType, edmSignatories } = currentNew;
  let item = {
    id,
    title: hansardData || edmSignatories ? title : questionFor,
    objectID: objectId,
    type: objectType,
  };

  const ShareOptions = {
    item,
    id: item.id,
    title: item.title,
    shareButtonWidget,
    itemShare: embed,
  };
  if (canonicalUrl) {
    ShareOptions.link = `${import.meta.env.VITE_APP_APPURL}${canonicalUrl}`;
  }
  return <Share {...ShareOptions} />;
};

const CommitteePublicationNewContent = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { search } = state;

  const { activeSearch } = search;
  const {
    currentNew,
    scrollbarRef,
    shareButtonWidget,
    contentContainer,
    isScrolling,
    reportWidget,
    embed,
    hideSummary,
    scrolling,
    notShowShare,
  } = props;

  const { createMarkup } = useCreateMarkup();

  const itemClass = () => {
    switch (currentNew?.hansardData?.type?.toLowerCase()) {
      case 'commons':
        return 'legislation-commons';
      case 'lords':
        return 'legislation-lords';
      default:
        return 'legislation-grey';
    }
  };
  const renderTitle = () => {
    let title;
    if (!!currentNew.titleHighlighted && (activeSearch || shareButtonWidget || getUrlParam('search'))) {
      title = currentNew.titleHighlighted;
    } else {
      title = currentNew.title;
    }
    return title;
  };

  const committeeContentContainerRef = useRef(null);
  const hasAdjustedPDFScale = useRef(false);

  useEffect(() => {
    const container = committeeContentContainerRef.current;
    if (container && !hasAdjustedPDFScale.current) {
      const addTargetToAnchorElems = () => {
        let elements = document.querySelectorAll('a');
        if (elements.length > 1) {
          for (let i = 0; i < elements.length; i++) {
            let item = elements[i];
            item.setAttribute('target', '_blank');
          }
        }
      };
      adjustPDFScale(container);
      hasAdjustedPDFScale.current = true;
      addTargetToAnchorElems();
    }
  }, []);

  const titleSection = useRef(null);

  let { showFixedTitle, titleStyles } = useTitleScrolledStyles({
    contentContainer,
    titleSection,
    leftValue: 0,
  });

  const titleRef = useCallback((el) => {
    if (el && el?.scrollHeight > el?.clientHeight + 5) {
      el.setAttribute('data-tooltip-class-name', 'header-tooltip pricing-tooltip-description table-item-cell-tooltip');
      el.setAttribute('data-tooltip-content', el?.innerText);
      el.setAttribute('data-tooltip-id', 'general-tooltip');
    }
  }, []);

  return (
    <>
      <div ref={titleSection}>
        <div
          className={`section-title pb-2 ${
            reportWidget ? 'content-title-in-widget' : 'content-title-with-summary'
          } content-border-title`}
        >
          <h3
            className='main-title news-title bg-white content-item-title'
            dangerouslySetInnerHTML={createMarkup(renderTitle())}
            ref={titleRef}
          />
          <div className='row mx-0 justify-content-between align-items-center'>
            <p className='heading-date mb-0'>
              {(currentNew.house || currentNew.contentType) && (
                <>
                  <span className={`${itemClass()}`}>{currentNew.house || currentNew.contentType}</span>
                  <span className='dot'></span>
                </>
              )}
              {currentNew.committee ? (
                <>
                  {currentNew.committee}
                  <span className='dot'></span>
                </>
              ) : (
                ''
              )}
              {currentNew.publicationType ? (
                <>
                  {currentNew.publicationType}
                  <span className='dot'></span>
                </>
              ) : (
                ''
              )}
              <span className='date'>{time(currentNew.publishDate).formatLongDate()}</span>
              {!!currentNew.sourceUrl && isValidURL(currentNew?.sourceUrl) && (
                <>
                  <span className='dot' />
                  <a
                    className='legislation-source mb-0'
                    href={currentNew.sourceUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {reportWidget ? 'Source' : 'Go to source'}
                  </a>
                </>
              )}
              <ContentPagePriorityBadge currentNew={currentNew} />
            </p>
          </div>
          {currentNew.tabledOn && <p className='heading-date mb-2'>Tabled in the {currentNew.session} session</p>}

          <div className='my-2 flex-centered justify-content-between'>
            {!reportWidget && <RateUpdatesContentButton currentNew={currentNew} />}
            {!notShowShare && (
              <div className='flex-centered'>
                <ShareButtonRender currentNew={currentNew} shareButtonWidget={shareButtonWidget} embed={embed} />
              </div>
            )}
          </div>
        </div>
      </div>
      <ContentSummary content={currentNew} hide={hideSummary} />
      <div className='pb-3 pb-lg-4 pb-lg-0 specific-content-container'>
        {/* FIXED TITLE */}
        <div
          className={`content-item-scrolled-title main-sticky section-title bg-white parliamentary-scrolled-title ${
            showFixedTitle && !reportWidget ? 'item-scrolled-title-visible' : ''
          }`}
          style={titleStyles}
        >
          <div className='d-flex justify-content-between scrolled-title-container'>
            <h3
              className='main-title news-title bg-white content-item-title'
              dangerouslySetInnerHTML={createMarkup(renderTitle())}
              ref={titleRef}
            />
            {!notShowShare && (
              <>
                <ShareButtonRender currentNew={currentNew} shareButtonWidget={shareButtonWidget} />
              </>
            )}
          </div>
        </div>
        {/* END FIXED TITLE */}
        <TextSelector
          currentNew={currentNew}
          scrollbarRef={scrollbarRef}
          scrolling={scrolling || isScrolling}
          reportWidget={reportWidget}
        >
          <div className='pt-3 news-content-in parliamentary-content-in context committee-publication-content'>
            {currentNew.body && (
              <div className='debate-item-contributiondebateitem'>
                <div
                  className='explanatory-container'
                  dangerouslySetInnerHTML={createMarkup(currentNew.body)}
                  ref={committeeContentContainerRef}
                />
              </div>
            )}
          </div>
        </TextSelector>
      </div>
    </>
  );
};

export default CommitteePublicationNewContent;
